import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Common } from '@thecvlb/design-system';
import { useFlag } from '@unleash/proxy-client-react';
import classNames from 'classnames';

import { ExtendedPaymentFormFields } from 'pages/SignUp/regularFlows/payment/PaymentExtended/paymentExtended.types';

import CheckboxLabel from 'features/CheckboxLabel';
import EncryptedBadge from 'shared/EncryptedBadge';
import BirthInput from 'shared/form/BirthInput';
import EmailInput from 'shared/form/EmailInput';
import TextInput from 'shared/form/TextInput';
import PaymentFormNew from 'widgets/PaymentFormNew';
import ChargifyForm from 'widgets/signUp/ChargifyForm';

import { PATIENTS_AGE_LIMITS } from 'utils/constants';
import { FeatureFlag, FlowTypes } from 'utils/enums';
import { validateBirth } from 'utils/helpers';
import { EMAIL_REGEXP } from 'utils/regExp';

import { PaymentFormProps } from './paymentForm.types';

import PaypalSvg from 'assets/icons/paypal.svg?react';

const PaymentForm: React.FC<PaymentFormProps> = ({
  onFormSubmit,
  onPaypalSubmit,
  defaultValues,
  loading,
  showEmailField = false,
  isShowBirthInput = true,
  isShowLegalName = false,
  flow,
  placement,
  showHeading = true,
  isOneTimeDiscount = true
}) => {
  const [showDummyPaypal, setShowDummyPaypal] = useState(false);
  const isEnabledPaypalOption = useFlag(FeatureFlag.Paypal);

  const { handleSubmit, formState, control, getValues, reset, watch, trigger } =
    useForm<ExtendedPaymentFormFields>({
      criteriaMode: 'all',
      mode: 'onTouched',
      shouldFocusError: false
    });

  const paymentDetailsClassName = 'text-mLg md:text-xl text-primary-700 font-bold mb-4';

  const fieldsToWatch: (keyof ExtendedPaymentFormFields)[] = [];
  if (showEmailField) {
    fieldsToWatch.push('email');
  }
  if (isShowBirthInput) {
    fieldsToWatch.push('dob');
  }
  const handleCheckPersonalInfo = () => {
    const dob = getValues('dob');
    const email = getValues('email');
    const fn = getValues('firstName');
    const ln = getValues('lastName');

    const isDobValid = validateBirth(
      dob,
      `Unfortunately, we are currently unable to provide care for patients who are under the age of ${PATIENTS_AGE_LIMITS.MIN}, or over the age of ${PATIENTS_AGE_LIMITS.MAX}.`,
      true
    );
    const isEmailValid = EMAIL_REGEXP.test(email);
    if (
      (isShowBirthInput && isDobValid !== true) ||
      (showEmailField && !isEmailValid) ||
      (isShowLegalName && (!fn || !ln))
    ) {
      setShowDummyPaypal(true);
    } else {
      setShowDummyPaypal(false);
    }
  };

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, []);

  useEffect(handleCheckPersonalInfo, [watch(fieldsToWatch)]);

  return (
    <>
      {(isShowBirthInput || showEmailField) && (
        <div className="mb-6 flex flex-col">
          <h4 className={paymentDetailsClassName}>Personal details</h4>
          {isShowLegalName && (
            <>
              <div className="mb-4 flex gap-4 md:mb-6">
                <TextInput
                  control={control}
                  dataTestId="first_name"
                  label="Legal name"
                  name="firstName"
                  placeholder="First name"
                  isRequired
                />
                <TextInput
                  control={control}
                  dataTestId="last_name"
                  label="&nbsp;"
                  name="lastName"
                  placeholder="Last name"
                  isRequired
                />
              </div>
            </>
          )}
          {isShowBirthInput && (
            <BirthInput
              control={control}
              customErrorMessage="LifeMD is currently available for patients between the age of 18 and 63."
              hint="LifeMD is currently available for patients between the age of 18 and 63."
              name="dob"
              maxAgeValidation
            />
          )}
          {showEmailField && (
            <div className="mb-7 mt-2 w-full md:mb-4" data-testid="email_field">
              <EmailInput
                control={control}
                label="Email"
                name="email"
                placeholder={'Email address'}
              />
            </div>
          )}
        </div>
      )}

      {showHeading && (
        <h4 className={paymentDetailsClassName} data-testid="payment_details">
          Payment
        </h4>
      )}
      {isEnabledPaypalOption && !!onPaypalSubmit && (
        <>
          <div>
            {showDummyPaypal && (
              <button
                className="w-full rounded-xl bg-[#FFC33A] p-2 disabled:opacity-25"
                disabled={loading}
                onClick={() => {
                  isShowBirthInput && trigger('dob');
                  showEmailField && trigger('email');
                  isShowLegalName && trigger(['firstName', 'lastName']);
                }}
              >
                <PaypalSvg className="ml-[40%] h-[22px] md:ml-[42%]" />
              </button>
            )}
            <div className={classNames({ 'invisible hidden': showDummyPaypal })}>
              <ChargifyForm
                onHandlePaypalToken={(token) => onPaypalSubmit({ token, ...getValues() })}
              />
            </div>

            <div className="flex items-center gap-4 py-4">
              <div className="h-px grow bg-gray-200"></div>
              <span className="uppercase text-gray">or</span>
              <div className="h-px grow bg-gray-200"></div>
            </div>
          </div>
        </>
      )}
      <form
        className="mb-7 flex-wrap md:mb-4"
        data-testid="chargify_form"
        onSubmit={handleSubmit(onFormSubmit)}
      >
        <PaymentFormNew control={control} getValues={getValues} />
        <Common.Button
          className="mx-auto my-4"
          color="blue"
          dataTestId="submit_payment_btn"
          disabled={!!Object.keys(formState.errors).length || loading}
          isLoading={loading}
          fullWidthOnMobile
        >
          Submit payment
        </Common.Button>
      </form>
      <EncryptedBadge />
      <div className="my-4">
        {placement === 'compound' ? (
          <span className="block text-center text-mSm text-gray md:text-sm">
            By placing your order, you agree to the LifeMD terms of service, insurance notice, and
            refund policy. The LifeMD Weight Management Program includes Semaglutide medication when
            medically appropriate, ongoing medical support for monthly prescription renewals, and
            lab work.
          </span>
        ) : placement === 'wm-flow-checkout' ? (
          <span className="block text-center text-mSm text-gray md:text-sm">
            By placing your order, you agree to the LifeMD terms of service
            {flow === FlowTypes.WeightManagementFlowMedicaid ||
            flow === FlowTypes.WeightManagementBalladHealth
              ? ''
              : ', insurance notice'}
            , and refund policy. Medication cost is not covered by the program. The LifeMD Weight
            Management Program includes prescriptions to{' '}
            {flow === FlowTypes.TripleTherapy ? 'weight loss' : 'GLP-1'} medications when medically
            appropriate, ongoing medical support for prescription renewals each month, dosage
            increase guidance
            {flow === FlowTypes.WeightManagementBalladHealth ? '.' : ', and lab work.'}
            {isOneTimeDiscount && ' Discount only applies to the first billing period.'}
          </span>
        ) : (
          <CheckboxLabel placement="payment-card" />
        )}
      </div>
    </>
  );
};

export default PaymentForm;
